import {
  StartingMaterialDatasetScalarFieldEnumSchema,
  StartingMaterialDatasetSchema as StartingMaterialDatasetSchemaRaw,
} from '@retro-ui/prisma';
import { z } from 'zod';

import { buildOrderBySchema } from '../common';

export const StartingMaterialDatasetSchema = StartingMaterialDatasetSchemaRaw.extend({
  size: z.coerce.bigint(),
});

export const DatasetQuerySchema = StartingMaterialDatasetSchema.pick({
  name: true,
  fileName: true,
  uploadedAt: true,
});
export const OrderBySchema = buildOrderBySchema(
  StartingMaterialDatasetScalarFieldEnumSchema.extract(['name', 'size'])
);

export enum DatasetStatus {
  INCOMPLETE = 'INCOMPLETE',
  PROCESSING = 'PROCESSING',
  ERROR = 'ERROR',
  NOT_FOUND = 'NOT_FOUND',
  READY = 'READY',
  SENDING = 'SENDING',
  CANCELED = 'CANCELED',
  REMOVED = 'REMOVED',
}

export const DatasetUploadResumeSchema = z.object({
  uploadId: z.string(),
  ownerId: z.string(),
});
export const DatasetUploadCompleteSchema = z.object({
  uploadId: z.string(),
  parts: z.array(
    z.object({
      partNumber: z.number(),
      etag: z.string(),
    })
  ),
  ownerId: z.string(),
});

export const DatasetBaseSchema = StartingMaterialDatasetSchema.extend({
  extendedId: z.string(),
  isPrivate: z.boolean(),
  initiallySelected: z.boolean(),
});
export type DatasetBase = z.infer<typeof DatasetBaseSchema>;

export const DatasetSupplierSchema = z.object({
  // id: z.union([z.string(), z.number()]),
  id: z.string(),
  name: z.string(),
});

export const DatasetErrorsSchema = z.object({
  csvErrors: z.array(
    z.object({
      errorType: z.string(),
      line: z.number(),
      details: z.string().nullish(),
    })
  ),
  smilesErrors: z.array(
    z.object({
      errorType: z.string(),
      smiles: z.string(),
      line: z.number(),
      details: z.string().nullish(),
    })
  ),
  errorLimitReached: z.boolean(),
});
export type DatasetErrors = z.infer<typeof DatasetErrorsSchema>;

export const DatasetMetadataSchema = z.object({
  status: z.nativeEnum(DatasetStatus),
  suppliers: z.record(z.string()).nullish(),
  errors: DatasetErrorsSchema.nullish(),
});
export type DatasetMetadata = z.infer<typeof DatasetMetadataSchema>;

export const DatasetIncompletePartSchema = z.object({
  partNumber: z.number(),
  url: z.string(),
});
export const DatasetUploadedPartSchema = z.object({
  partNumber: z.number(),
  etag: z.string(),
  size: z.number(),
});
export const DatasetUploadMetadataSchema = z
  .object({
    uploadId: z.string(),
    remainingParts: z.array(DatasetIncompletePartSchema),
    partSize: z.number(),
    uploadedParts: z.array(DatasetUploadedPartSchema),
  })
  .partial();

export const PredefinedDatasetOptionsSchema = z
  .object({
    provider: z.string(),
  })
  .partial();

export const DatasetUniqueResultSchema = DatasetBaseSchema.merge(DatasetMetadataSchema)
  .merge(DatasetUploadMetadataSchema)
  .merge(PredefinedDatasetOptionsSchema);
export type DatasetFetchUniqueResult = z.infer<typeof DatasetUniqueResultSchema>;

export const DatasetCompoundInformationSchema = z.object({
  originalSmiles: z.string(),
  normalizedSmiles: z.string(),
  supplierId: z.string().nullish(),
  availabilityTier: z.number().nullish(),
  amount: z.number().nullish(),
  price: z.number().nullish(),
  compoundName: z.string().nullish(),
  casNumber: z.string().nullish(),
  versionId: z.string().nullish(),
  datasetId: z.string(),
  datasetName: z.string().nullish(),
  supplierName: z.string().nullish(),
});
export type DatasetCompoundInformation = z.infer<typeof DatasetCompoundInformationSchema>;

export const PriceFetchSchema = z.array(z.array(DatasetCompoundInformationSchema));
export type PricingFetchResult = z.infer<typeof PriceFetchSchema>;

export const DatasetCreateResultSchema = DatasetBaseSchema.merge(
  DatasetMetadataSchema.pick({ status: true })
).merge(DatasetUploadMetadataSchema.pick({ uploadId: true, remainingParts: true, partSize: true }));
export type DatasetCreateResult = z.infer<typeof DatasetCreateResultSchema>;

export const DatasetResumeResultSchema = DatasetBaseSchema.merge(
  DatasetMetadataSchema.pick({ status: true })
).merge(
  DatasetUploadMetadataSchema.pick({
    uploadId: true,
    remainingParts: true,
    partSize: true,
    uploadedParts: true,
  })
);
export type DatasetResumeResult = z.infer<typeof DatasetResumeResultSchema>;

export const DatasetCompleteResultSchema = DatasetBaseSchema.merge(
  DatasetMetadataSchema.pick({ status: true })
);
export type DatasetCompleteResult = z.infer<typeof DatasetCompleteResultSchema>;

export const DatasetFetchResultSchema = z.object({
  pagination: z.object({
    total: z.number(),
  }),
  data: z.array(DatasetUniqueResultSchema),
});
export type DatasetFetchResult = z.infer<typeof DatasetFetchResultSchema>;
